import * as datadogRum from 'lib/telemetry/datadogRum';

const BYTES_PER_MB = 1_048_576;
const MS_PER_S = 1_000;

class MemoryMonitor {
    private sessionStartedAt = 0;
    private sampleIndex = 0;
    private previousHeapUsedMb: number | null = null;
    private baselineHeapUsedMb: number | null = null;

    /** Initializes session state. Does not start a timer — the context manager owns the tick. */
    start(): void {
        this.sessionStartedAt = Date.now();
        this.sampleIndex = 0;
        this.baselineHeapUsedMb = null;
    }

    sample = (): void => {
        if (document.visibilityState === 'hidden') return;

        const memory = (
            performance as Performance & {
                memory?: {
                    usedJSHeapSize: number;
                    totalJSHeapSize: number;
                    jsHeapSizeLimit: number;
                };
            }
        ).memory;

        const heapUsedMb = memory ? memory.usedJSHeapSize / BYTES_PER_MB : null;
        const heapTotalMb = memory ? memory.totalJSHeapSize / BYTES_PER_MB : null;
        const heapLimitMb = memory ? memory.jsHeapSizeLimit / BYTES_PER_MB : null;
        const heapDeltaMb =
            heapUsedMb !== null && this.previousHeapUsedMb !== null ? heapUsedMb - this.previousHeapUsedMb : null;
        this.previousHeapUsedMb = heapUsedMb;

        if (heapUsedMb !== null && this.baselineHeapUsedMb === null) {
            this.baselineHeapUsedMb = heapUsedMb;
        }

        const heapGrowthPct =
            heapUsedMb !== null && this.baselineHeapUsedMb !== null && this.baselineHeapUsedMb > 0
                ? ((heapUsedMb - this.baselineHeapUsedMb) / this.baselineHeapUsedMb) * 100
                : null;

        try {
            datadogRum.addAction('memory_sample', {
                heap_used_mb: heapUsedMb,
                heap_total_mb: heapTotalMb,
                heap_limit_mb: heapLimitMb,
                heap_delta_mb: heapDeltaMb,
                heap_baseline_mb: this.baselineHeapUsedMb,
                heap_growth_pct: heapGrowthPct,
                dom_node_count: document.getElementsByTagName('*').length,
                session_elapsed_s: Math.round((Date.now() - this.sessionStartedAt) / MS_PER_S),
                sample_index: this.sampleIndex++,
            });
        } catch {
            // Datadog may not be initialized
        }
    };
}

export const memoryMonitor = new MemoryMonitor();
